import type { AppError, ErrorDisplayMode } from "./types";


/**
 * Defines the context in which an error occurred, which can influence how the error should be presented to the user. This allows for more nuanced error handling based on the specific circumstances of the error.
 * 
 * - "mutation": Errors that occur during user-initiated actions, such as form submissions or button clicks. These errors often require immediate feedback to the user, so they may be displayed inline or as a toast notification.
 * - "initialLoad": Errors that occur during the initial loading of a page or component. These errors are critical for user experience and may warrant displaying a dedicated error page.
 * - "backgroundQuery": Errors that occur during background data fetching or processing. These errors are less critical and can often be handled with less intrusive notifications, such as toasts.
 * - "routeLoad": Errors that occur during route changes or navigation. Similar to initial load errors, these can significantly impact user experience and may require displaying an error page.
 */
export type ErrorContext = "mutation" | "initialLoad" | "backgroundQuery" | "routeLoad";



/**
 * Determines the appropriate display mode for an error based on its kind and context.
 * @param error AppError
 * @param context ErrorContext
 * @returns ErrorDisplayMode
 */
export function presentError(error: AppError, context: ErrorContext): ErrorDisplayMode {
    
  if (error.kind === "auth") {
    return "page";
  }

  if (error.kind === "forbidden") {
    return "page";
  }

  switch (context) {

    case "mutation": // Mutations are user-initiated actions, so we want to provide immediate feedback.

      if (error.kind === "conflict") {
        return "inline";
      }

      if (error.kind === "business") {
        return "toast";
      }

      if (error.defaultDisplayMode !== "boundary") {
        return error.defaultDisplayMode;
      }

      return "toast";

    case "initialLoad": // Initial loads are critical for user experience, so we want to ensure errors are prominently displayed.
    case "routeLoad": // Route loads are also critical, as they affect navigation, so we treat them similarly to initial loads.
      if (error.kind === "notFound") {
        return "page";
      }

      if (error.kind === "server" || error.kind === "network") {
        return "page";
      }

      return error.defaultDisplayMode;

    case "backgroundQuery": // Background queries are less critical, so we can be more lenient in how we display errors.
      if (error.defaultDisplayMode === "page" || error.defaultDisplayMode === "boundary") {
        return "toast";
      }

      return error.defaultDisplayMode;

    default:
      return error.defaultDisplayMode;
  }
}
